import { useEffect, useCallback, useRef } from "react";
import {
  saveDraftToIndexedDB,
  getDraftFromIndexedDB,
  deleteDraftFromIndexedDB,
} from "../utils/draftStorage";

/**
 * Hook to manage crash recovery and debounced autosaves.
 * Uses browser IndexedDB for heavy project payloads (.sb3 binaries, costumes, audio)
 * and lightweight localStorage markers (< 200B) to eliminate QuotaExceededError crashes completely.
 */
export const useLocalDraftAutoSave = ({
  selectedChild,
  activeWorkspaceEnv,
  activeWorkspaceCode,
  activeWorkspaceFiles,
  projectName,
  projectId,
  lastSavedCloudCode,
  baselineCode,
  setProjectName,
  setProjectId,
  setLoadedProjectData,
  setActiveWorkspaceCode,
  setActiveWorkspaceFiles,
  setLastSavedCloudCode,
  setBaselineCode,
  openDialog,
}) => {
  const getDraftKey = useCallback(() => {
    if (!selectedChild) return null;
    const cid = selectedChild.id || selectedChild.uid;
    return `practido_draft_${cid}_${activeWorkspaceEnv}`;
  }, [selectedChild, activeWorkspaceEnv]);

  const latestDraftRef = useRef(null);

  const restoreDraftData = useCallback(
    (parsed) => {
      if (!parsed) return;
      setProjectName(parsed.projectName || "Unnamed Project");
      setProjectId(parsed.projectId || null);

      if (Array.isArray(parsed.files) && parsed.files.length > 0) {
        if (setActiveWorkspaceFiles) setActiveWorkspaceFiles(parsed.files);
        const entryFile = parsed.files.find((f) => f.isEntry) || parsed.files[0];
        const content = parsed.code || entryFile?.content || null;
        setLoadedProjectData({ code: content, files: parsed.files });
        setActiveWorkspaceCode(content);
      } else {
        if (setActiveWorkspaceFiles) setActiveWorkspaceFiles(null);
        setLoadedProjectData(parsed.code || null);
        setActiveWorkspaceCode(parsed.code || null);
      }

      setLastSavedCloudCode(parsed.lastSavedCloudCode || null);
      setBaselineCode(parsed.baselineCode || null);
    },
    [
      setProjectName,
      setProjectId,
      setLoadedProjectData,
      setActiveWorkspaceCode,
      setActiveWorkspaceFiles,
      setLastSavedCloudCode,
      setBaselineCode,
    ]
  );

  const clearDraft = useCallback(() => {
    const draftKey = getDraftKey();
    if (draftKey) {
      deleteDraftFromIndexedDB(draftKey).catch(() => null);
      try {
        localStorage.removeItem(draftKey);
      } catch (_) {}
    }
    latestDraftRef.current = null;
  }, [getDraftKey]);

  // 1. Check for unsaved draft in IndexedDB (with legacy localStorage fallback)
  useEffect(() => {
    const draftKey = getDraftKey();
    if (!draftKey) return;

    let isMounted = true;

    (async () => {
      try {
        // Priority 1: Check modern IndexedDB for full project draft (including assets)
        let parsed = await getDraftFromIndexedDB(draftKey);

        // Priority 2: Fallback to legacy localStorage draft if IDB is empty
        if (!parsed) {
          try {
            const legacyDraftStr = localStorage.getItem(draftKey);
            if (legacyDraftStr) {
              const legacyDraft = JSON.parse(legacyDraftStr);
              if (legacyDraft && legacyDraft.code) {
                parsed = legacyDraft;
              }
            }
          } catch (_) {}
        }

        if (parsed && isMounted) {
          const normalize = (c) =>
            typeof c === "object" ? JSON.stringify(c) : String(c || "");
          const isDraftDirty =
            normalize(parsed.code) !== normalize(parsed.baselineCode);

          if (isDraftDirty && parsed.code) {
            openDialog({
              type: "RESTORE_DRAFT",
              title: "Unsaved Work Found",
              message:
                "We found an unsaved draft from your last session. Would you like to restore it, or start a completely new project?",
              confirmText: "Restore",
              confirmClass: "bg-purple-600 hover:bg-purple-500 text-white",
              cancelText: "Start new",
              payload: parsed,
              showInput: false,
              inputValue: "",
            });
          } else {
            restoreDraftData(parsed);
          }
        }
      } catch (e) {
        console.warn("Failed to check for unsaved draft:", e);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [selectedChild, activeWorkspaceEnv]); // eslint-disable-line

  // 2. Debounced save to IndexedDB (4 seconds of user inactivity)
  useEffect(() => {
    if (!activeWorkspaceCode) return;

    const draftKey = getDraftKey();
    if (!draftKey) return;

    const draft = {
      draftKey,
      projectName,
      projectId,
      code: activeWorkspaceCode,
      files: activeWorkspaceFiles || undefined,
      lastSavedCloudCode,
      baselineCode,
      timestamp: Date.now(),
    };
    latestDraftRef.current = draft;

    const timer = setTimeout(() => {
      // 1. Save heavy full payload into IndexedDB
      saveDraftToIndexedDB(draftKey, draft).catch((err) => {
        console.warn("IndexedDB autosave draft error:", err);
      });

      // 2. Store only lightweight metadata marker in localStorage
      try {
        const metadataMarker = {
          hasDraft: true,
          projectName: projectName || "Unnamed Project",
          projectId: projectId || null,
          timestamp: Date.now(),
          environment: activeWorkspaceEnv,
        };
        localStorage.setItem(draftKey, JSON.stringify(metadataMarker));
      } catch (lsErr) {
        console.warn("localStorage marker write skipped (quota limit):", lsErr);
      }
    }, 4000);

    return () => clearTimeout(timer);
  }, [
    activeWorkspaceCode,
    activeWorkspaceFiles,
    projectName,
    projectId,
    lastSavedCloudCode,
    baselineCode,
    activeWorkspaceEnv,
    getDraftKey,
  ]);

  // 3. Flush latest draft before tab close / refresh
  useEffect(() => {
    const handleBeforeUnload = () => {
      const draftKey = getDraftKey();
      if (draftKey && latestDraftRef.current) {
        saveDraftToIndexedDB(draftKey, latestDraftRef.current).catch(() => null);
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [getDraftKey]);

  return {
    getDraftKey,
    restoreDraftData,
    clearDraft,
  };
};

export default useLocalDraftAutoSave;
